import axios, { CancelTokenSource } from 'axios';

import { PublicHolidays, PublicHolidaysResponse, PublicHolidaysResponseDecoder } from '~hooks/usePublicHoliday/domain';
import { APIError, UnsupportedStructureError } from '~services/Errors';

const formatHolidayPayload = (data: PublicHolidays): PublicHolidays => ({
  ...data,
  postcodes: Array.isArray(data.postcodes)
    ? data.postcodes
    : !data.postcodes
    ? []
    : String(data.postcodes)
        .split(',')
        .map((pc) => pc.trim())
        .filter((pc) => pc !== ''),
});

export type GetPublicHolidaysOptions = {
  search?: string;
  timezones?: string[];
  postcode?: string;
  year: number;
};

export type GetPublicHolidaysByNextUrlOptions = {
  cancelToken?: CancelTokenSource;
};

export const getPublicHolidaysByNextUrl = async (
  nextUrl: string,
  { cancelToken }: GetPublicHolidaysByNextUrlOptions = {},
): Promise<PublicHolidaysResponse | undefined> => {
  let resp;

  try {
    resp = await axios.request({
      url: nextUrl,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      cancelToken: cancelToken ? cancelToken.token : undefined,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return undefined;
    }

    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = PublicHolidaysResponseDecoder.run(resp.data);

  if (!decoded.ok) {
    const err = new UnsupportedStructureError(decoded.error.message);
    console.error(decoded.error);
    console.error(err);
    throw err;
  }

  return decoded.result;
};

export const getPublicHolidays = async ({
  search,
  timezones,
  postcode,
  year,
}: GetPublicHolidaysOptions): Promise<PublicHolidaysResponse> => {
  let path = `/api/public-holidays/?`;
  if (search) path += `search=${search}&`;
  if (timezones && timezones.length > 0) {
    timezones.forEach((tz) => {
      path += `timezone=${tz}&`;
    });
  }
  if (postcode) path += `postcode=${postcode}&`;
  path += `year=${year}&`;

  let resp;

  try {
    resp = await axios.request({
      url: path,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, e.message);
    }

    throw new APIError(-1, e as string);
  }

  const decoded = PublicHolidaysResponseDecoder.run(resp.data);

  if (!decoded.ok) {
    const err = new UnsupportedStructureError(decoded.error.message);

    throw err;
  }

  return decoded.result;
};

export const updatePublicHoliday = async (oldDate: string, oldName: string, data: PublicHolidays): Promise<void> => {
  const path = `/api/public-holidays/`;
  const payload = { ...formatHolidayPayload(data), old_date: oldDate, old_name: oldName };

  try {
    await axios.request({
      method: 'PATCH',
      url: path,
      data: payload,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export const removePublicHoliday = async (data: PublicHolidays): Promise<void> => {
  const path = '/api/public-holidays/';

  try {
    await axios.request({
      method: 'DELETE',
      url: path,
      data: data,
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      // Response should always be defined if axios error
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }

    throw new APIError(-1, e as string);
  }
};

export type UploadPublicHolidaysResponse = {
  inserted: number;
  total: number;
};

export type UploadValidationFailure = {
  row: number;
  field: string;
  reason: string;
};

export const uploadPublicHolidays = async (
  file: File,
  onUploadProgress: (progress: number) => void,
): Promise<UploadPublicHolidaysResponse> => {
  const path = '/api/public-holidays/upload';

  const formData = new FormData();
  formData.append('file', file);

  let resp;
  try {
    resp = await axios.request({
      method: 'POST',
      url: path,
      data: formData,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total === undefined) return;
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onUploadProgress(percentCompleted);
      },
      headers: {
        Accept: 'application/json',
      },
    });
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const data = e.response!.data as { error: string; failures?: UploadValidationFailure[] };
      const err = new APIError(e.response!.status, data.error || e.message);
      (err as any).failures = data.failures ?? [];
      throw err;
    }
    throw new APIError(-1, e as string);
  }

  return resp.data as UploadPublicHolidaysResponse;
};

export const downloadPublicHolidays = async (year: number): Promise<void> => {
  const path = `/api/public-holidays/download?year=${year}`;

  try {
    const resp = await axios.request({
      method: 'GET',
      url: path,
      responseType: 'blob',
      headers: {
        Accept: 'application/csv',
      },
    });

    //Create a temporary anchor and trigger browser download
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `public_holidays_${year}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    if (axios.isAxiosError(e)) {
      throw new APIError(e.response!.status, (e.response!.data as { error: string }).error || e.message);
    }
    throw new APIError(-1, e as string);
  }
};
